import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, MapPin, Search, X } from 'lucide-react';
import { useBillboardCitiesInfinite } from '@/features/campaigns/api/useCampaignMutations';
import type { BillboardCityOption } from '@/features/campaigns/api/campaignApi';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { FieldError } from '@/features/campaigns/components/FormPrimitives';
import { FloatingOverlay } from '@/shared/ui/FloatingOverlay';

type TargetedCitiesProps = {
  selectedCities: string[];
  onChange: (cities: string[]) => void;
  error?: string;
};

type CityRow = {
  city: string;
  screenCount: number;
  comingSoon: boolean;
};

function isComingSoonStatus(status?: string) {
  const s = String(status || '').toLowerCase();
  return (
    s === 'upcoming' ||
    s.includes('coming') ||
    s.includes('onboarding') ||
    s.includes('soon')
  );
}

function formatScreenMeta(count: number, comingSoon: boolean) {
  if (comingSoon) return 'No screens yet';
  if (count > 0) return `${count} ${count === 1 ? 'screen' : 'screens'}`;
  return 'Live inventory';
}

export function TargetedCities({
  selectedCities,
  onChange,
  error,
}: TargetedCitiesProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debounced = useDebounce(search, 400);
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const query = useBillboardCitiesInfinite(debounced, open);
  const cities = useMemo<CityRow[]>(() => {
    const pages = query.data?.pages ?? [];
    const rows = pages.flatMap((p) =>
      (p.data ?? [])
        .map((c: BillboardCityOption) => {
          const name = String(c.city || '').trim();
          if (!name) return null;
          return {
            city: name,
            screenCount: Number(c.screenCount) || 0,
            comingSoon: isComingSoonStatus(c.status),
          } satisfies CityRow;
        })
        .filter((c): c is CityRow => Boolean(c)),
    );
    const byName = new Map<string, CityRow>();
    for (const row of rows) {
      const key = row.city.toLowerCase();
      const prev = byName.get(key);
      if (!prev || (!row.comingSoon && prev.comingSoon)) {
        byName.set(key, row);
      } else if (prev && row.screenCount > prev.screenCount) {
        byName.set(key, row);
      }
    }
    return [...byName.values()];
  }, [query.data]);

  useEffect(() => {
    if (!open) {
      setSearch('');
      return;
    }
    const id = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [open]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !open) return;
    const io = new IntersectionObserver((entries) => {
      if (
        entries[0]?.isIntersecting &&
        query.hasNextPage &&
        !query.isFetchingNextPage
      ) {
        void query.fetchNextPage();
      }
    });
    io.observe(el);
    return () => io.disconnect();
  }, [open, query]);

  const close = () => {
    setOpen(false);
    setSearch('');
  };

  const toggle = (city: string) => {
    if (selectedCities.includes(city)) {
      onChange(selectedCities.filter((c) => c !== city));
    } else {
      onChange([...selectedCities, city]);
    }
  };

  return (
    <div ref={rootRef} className="relative flex w-full flex-col gap-1.5 text-left">
      <div className="flex items-baseline justify-between gap-2">
        <label className="text-[13px] font-semibold tracking-[-0.01em] text-[var(--dash-ink)]">
          Target cities
          <span className="ml-1 text-red-500" aria-hidden>
            *
          </span>
        </label>
        {selectedCities.length > 0 ? (
          <span className="text-[11px] font-medium tabular-nums text-[var(--dash-muted)]">
            {selectedCities.length}{' '}
            {selectedCities.length === 1 ? 'city' : 'cities'}
          </span>
        ) : null}
      </div>
      <p className="m-0 -mt-0.5 text-[11px] leading-snug text-[var(--dash-muted)]">
        Screens will be suggested in these cities
      </p>

      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-invalid={Boolean(error)}
        onClick={() => setOpen(true)}
        className={`mb-city-trigger group flex min-h-[52px] w-full items-center gap-3 rounded-[14px] border px-3 py-2.5 text-left outline-none transition duration-200 ${
          error
            ? 'border-red-300 bg-red-50/50'
            : open
              ? 'border-[var(--dash-brand)]/40 bg-white shadow-[0_0_0_3px_rgba(41,121,255,0.12)]'
              : 'border-black/[0.08] bg-[#F7F8FA] hover:border-black/[0.14] hover:bg-white'
        }`}
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white shadow-[0_1px_2px_rgba(16,38,73,0.08)] ring-1 ring-black/[0.04]">
          <MapPin
            className="h-3.5 w-3.5 text-[var(--dash-brand)]"
            aria-hidden
          />
        </span>

        <div className="min-w-0 flex-1">
          {selectedCities.length === 0 ? (
            <span className="text-[13px] text-[#8E98A8]">
              Select one or more cities
            </span>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {selectedCities.map((city) => (
                <span
                  key={city}
                  className="inline-flex max-w-full items-center gap-1 rounded-full bg-white py-1 pl-2.5 pr-1 text-[12px] font-semibold text-[var(--dash-ink)] shadow-[0_1px_2px_rgba(16,38,73,0.06)] ring-1 ring-black/[0.06]"
                >
                  <span className="truncate">{city}</span>
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label={`Remove ${city}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggle(city);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        toggle(city);
                      }
                    }}
                    className="inline-flex h-5 w-5 items-center justify-center rounded-full text-[var(--dash-muted)] transition hover:bg-black/[0.06] hover:text-[var(--dash-ink)]"
                  >
                    <X className="h-3 w-3" />
                  </span>
                </span>
              ))}
            </div>
          )}
        </div>

        <ChevronDown
          className={`h-4 w-4 shrink-0 text-[#9AA8B8] transition duration-200 ${
            open ? 'rotate-180 text-[var(--dash-brand)]' : 'group-hover:text-[var(--dash-ink)]'
          }`}
          aria-hidden
        />
      </button>

      <FloatingOverlay
        open={open}
        onClose={close}
        title="Target cities"
        subtitle="Pick markets with live screens — coming-soon cities stay listed for awareness."
        maxWidth={440}
      >
        <div className="flex h-[min(68vh,520px)] min-h-0 flex-col overflow-hidden">
          <div className="shrink-0 border-b border-black/[0.06] px-3 py-2.5 sm:px-4">
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--dash-muted)]"
                aria-hidden
              />
              <input
                ref={searchRef}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search cities"
                className="w-full rounded-[10px] border-0 bg-[#F2F4F7] py-2.5 pl-8 pr-3 text-[13px] text-[var(--dash-ink)] outline-none placeholder:text-[#8E98A8] focus:bg-white focus:ring-2 focus:ring-[var(--dash-brand)]/20"
              />
            </div>
          </div>

          <div
            className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 py-2 sm:px-2.5"
            role="listbox"
            aria-multiselectable="true"
            aria-label="Cities"
          >
            {query.isPending ? (
              <p className="px-3 py-8 text-center text-[13px] text-[var(--dash-muted)]">
                Loading cities…
              </p>
            ) : cities.length === 0 ? (
              <p className="px-3 py-8 text-center text-[13px] text-[var(--dash-muted)]">
                No cities match your search
              </p>
            ) : (
              cities.map((row) => {
                const { city, screenCount, comingSoon } = row;
                const active = selectedCities.includes(city);
                if (comingSoon) {
                  return (
                    <div
                      key={city}
                      role="option"
                      aria-selected={false}
                      aria-disabled="true"
                      className="mb-0.5 flex w-full items-center gap-2.5 rounded-[12px] px-2.5 py-2.5 text-left opacity-80"
                    >
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#EEF1F5] text-[#B0B8C4]">
                        <MapPin className="h-3.5 w-3.5" aria-hidden />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13px] font-medium text-[#8E98A8]">
                          {city}
                        </span>
                        <span className="mt-0.5 block text-[11px] text-[#A8B0BC]">
                          {formatScreenMeta(screenCount, true)}
                        </span>
                      </span>
                      <span className="shrink-0 rounded-full bg-[#FFF6E8] px-2 py-0.5 text-[10px] font-semibold tracking-wide text-[#C2410C] uppercase">
                        Coming soon
                      </span>
                    </div>
                  );
                }

                return (
                  <button
                    key={city}
                    type="button"
                    role="option"
                    aria-selected={active}
                    onClick={() => toggle(city)}
                    className={`mb-0.5 flex w-full items-center gap-2.5 rounded-[12px] px-2.5 py-2.5 text-left text-[13px] transition active:scale-[0.99] ${
                      active
                        ? 'bg-[var(--dash-brand)]/[0.08] font-semibold text-[var(--dash-ink)]'
                        : 'font-medium text-[var(--dash-ink)] hover:bg-black/[0.04]'
                    }`}
                  >
                    <span
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                        active
                          ? 'bg-[var(--dash-brand)] text-white'
                          : 'bg-[#EEF1F5] text-[var(--dash-muted)]'
                      }`}
                    >
                      <MapPin className="h-3.5 w-3.5" aria-hidden />
                    </span>
                    <span className="min-w-0 flex-1 text-left">
                      <span className="flex min-w-0 items-center gap-2">
                        <span className="truncate">{city}</span>
                        <span className="shrink-0 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-emerald-700 uppercase">
                          Available
                        </span>
                      </span>
                      <span className="mt-0.5 block text-[11px] font-medium tabular-nums text-[var(--dash-muted)]">
                        {formatScreenMeta(screenCount, false)}
                      </span>
                    </span>
                    <span
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full transition ${
                        active
                          ? 'bg-[var(--dash-brand)] text-white'
                          : 'border border-[#D5DBE5] bg-white'
                      }`}
                      aria-hidden
                    >
                      {active ? <Check className="h-3 w-3 stroke-[3]" /> : null}
                    </span>
                  </button>
                );
              })
            )}
            <div ref={sentinelRef} className="h-2" />
          </div>

          <div className="flex shrink-0 items-center justify-between gap-3 border-t border-black/[0.06] bg-[#FAFBFC] px-4 py-3">
            <span className="text-[12px] font-medium text-[var(--dash-muted)]">
              {selectedCities.length > 0
                ? `${selectedCities.length} selected`
                : 'Select at least one city'}
            </span>
            <button
              type="button"
              onClick={close}
              disabled={selectedCities.length === 0}
              className="rounded-full bg-[var(--dash-brand)] px-4 py-2 text-[12px] font-semibold text-white transition hover:opacity-95 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40"
            >
              Done
            </button>
          </div>
        </div>
      </FloatingOverlay>

      <FieldError message={error} />
    </div>
  );
}
